import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, Eye, EyeOff, AlertCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const Login = () => {
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const from = location.state?.from || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      await login(email.trim(), pwd);
      nav(from, { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bb-bg text-white flex items-center justify-center px-4 py-10 relative overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none"></div>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-gradient-to-br from-[#1c3df5]/20 to-transparent blur-3xl pointer-events-none"></div>
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3b59ff] to-[#1c3df5] flex items-center justify-center">
            <div className="w-3 h-3 rounded-full bg-white/95"></div>
          </div>
          <span className="font-display font-bold text-2xl tracking-tight">Northwind</span>
        </Link>
        <div className="bb-card rounded-3xl p-7 lg:p-8">
          <h1 className="font-display font-bold text-[26px] text-white mb-1">Welcome back</h1>
          <p className="text-[13px] text-white/55 mb-6">Sign in to your trading account.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-[12px] text-white/55 mb-1.5 block">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="you@example.com"
                className="w-full h-11 px-4 bg-white/[0.02] border border-white/10 rounded-xl text-[14px] text-white outline-none focus:border-[#1c3df5] placeholder:text-white/30"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[12px] text-white/55">Password</label>
                <Link to="/forgot-password" className="text-[12px] text-[#7d8eff] hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  type={show ? "text" : "password"}
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder="Your password"
                  className="w-full h-11 px-4 pr-10 bg-white/[0.02] border border-white/10 rounded-xl text-[14px] text-white outline-none focus:border-[#1c3df5] placeholder:text-white/30"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/45 hover:text-white"
                  tabIndex={-1}
                >
                  {show ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            {err && (
              <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-[12px] text-rose-300">
                <AlertCircle size={14} className="shrink-0 mt-0.5" />
                <span>{err}</span>
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-xl bb-blue text-white font-medium text-[14px] inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? "Signing in..." : (<>Sign in <ArrowRight size={15} /></>)}
            </button>
          </form>
          <div className="mt-6 pt-5 border-t border-white/5 text-center text-[13px] text-white/55">
            Don't have an account?{" "}
            <Link to="/signup" className="text-[#7d8eff] hover:underline font-medium">
              Create one
            </Link>
          </div>
        </div>
        <div className="text-center mt-5 text-[11px] text-white/35">
          By signing in you agree to our{" "}
          <Link to="/terms" className="text-white/55 hover:text-white hover:underline">Terms</Link> &{" "}
          <Link to="/privacy" className="text-white/55 hover:text-white hover:underline">Privacy Policy</Link>.
        </div>
      </div>
    </div>
  );
};

export default Login;
